/**
 * Iranian identifier validators — کد ملی, شناسهٔ ملی, کد اقتصادی, شبا (IBAN), کدپستی.
 *
 * ONE authority for "is this identifier well-formed", shared by the setup gate (company profile), customers &
 * suppliers, and مودیان invoices, so the same rule is never re-implemented (and can never disagree between
 * screens). Every validator normalizes Persian/Arabic digits first, ignores spaces/dashes, and rejects the
 * all-same-digit strings that pass a checksum but are never real data ("0000000000").
 *
 * The two national identifiers carry REAL check digits (ISO-style modulus schemes), so a typo is caught, not
 * merely a wrong length; the economic code and postal code have no public checksum, so they are validated by
 * length + digits only (a format/typo guard, not a authenticity proof).
 */
import { normalizeDigits } from './normalize.js';
/** ASCII digits only, after Persian/Arabic normalization (drops spaces, dashes, any non-digit). */
function digitsOnly(s) {
    return normalizeDigits(s).replace(/[^0-9]/g, '');
}
/** A run of one repeated digit ("0000000000") — passes most checksums but is never a real identifier. */
function allSameDigit(s) {
    return s.length > 0 && /^(\d)\1*$/.test(s);
}
/**
 * کد ملی — personal national code (10 digits). Modulus-11 check digit: Σ dᵢ·(10−i) for i=0..8, r = sum%11;
 * the 10th digit must equal r when r<2, else 11−r.
 */
export function isValidNationalId(input) {
    const s = digitsOnly(input);
    if (s.length !== 10 || allSameDigit(s))
        return false;
    const d = [...s].map(Number);
    let sum = 0;
    for (let i = 0; i < 9; i++)
        sum += d[i] * (10 - i);
    const r = sum % 11;
    const check = r < 2 ? r : 11 - r;
    return d[9] === check;
}
/**
 * شناسهٔ ملی اشخاص حقوقی — legal-entity national id (11 digits). Check digit d₁₀: with `dec = d₉ + 2` and the
 * repeating coefficients [29,27,23,19,17], r = (Σ (dᵢ+dec)·coefᵢ for i=0..9) % 11 (10 folds to 0) must equal d₁₀.
 */
export function isValidLegalId(input) {
    const s = digitsOnly(input);
    if (s.length !== 11 || allSameDigit(s))
        return false;
    const d = [...s].map(Number);
    const dec = d[9] + 2;
    const coef = [29, 27, 23, 19, 17, 29, 27, 23, 19, 17];
    let sum = 0;
    for (let i = 0; i < 10; i++)
        sum += (d[i] + dec) * coef[i];
    let r = sum % 11;
    if (r === 10)
        r = 0;
    return r === d[10];
}
/**
 * Accept EITHER a personal national code (10) or a legal-entity national id (11) — used where the entity type
 * isn't known up front (a company may be registered under a sole-proprietor's personal کد ملی).
 */
export function isValidNationalOrLegalId(input) {
    return isValidNationalId(input) || isValidLegalId(input);
}
/**
 * شبا / IBAN (Iran) — "IR" + 24 digits (26 chars). ISO 7064 MOD-97-10: move the first four chars to the end,
 * map letters A–Z→10–35, and the whole number mod 97 must be 1. A bare 24-digit BBAN is accepted and assumed
 * Iranian (prefixed with IR). Computed with an incremental remainder so no BigInt is needed.
 */
export function isValidIban(input) {
    let s = normalizeDigits(input).toUpperCase().replace(/[\s-]/g, '');
    if (/^\d{24}$/.test(s))
        s = `IR${s}`;
    if (!/^IR\d{24}$/.test(s))
        return false;
    const rearranged = s.slice(4) + s.slice(0, 4);
    let remainder = 0;
    for (const ch of rearranged) {
        const chunk = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
        for (const c of chunk)
            remainder = (remainder * 10 + (c.charCodeAt(0) - 48)) % 97;
    }
    return remainder === 1;
}
/**
 * کد اقتصادی — economic code. No public checksum, so a length + digits guard (11 or 12 digits, the documented
 * lengths) that catches a blank / typo without claiming authenticity.
 */
export function isValidEconomicCode(input) {
    const s = digitsOnly(input);
    return (s.length === 11 || s.length === 12) && !allSameDigit(s);
}
/** کدپستی — Iranian postal code: exactly 10 digits (no public checksum). */
export function isValidPostalCode(input) {
    const s = digitsOnly(input);
    return s.length === 10 && !allSameDigit(s);
}
//# sourceMappingURL=iranId.js.map